import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAnalysisStatus, uploadScreenshot } from '../../services/ai/uploadAnalysisApi';
import {
  formatUploadError,
  validateFileSize,
  validateFileType,
} from '../../utils/ai/uploadValidation';
import { getDashboardSessionId } from '../../utils/ai/sessionHelper';

const initialState = {
  file: null,
  uploadedFile: null,
  analysisId: null,
  uploadId: null,
  status: 'idle',
  uploadStatus: 'idle',
  summary: null,
  analysisResult: null,
  error: null,
  polling: false,
  statusLoading: false,
  uploadProgress: 0,
};

const normalizeUploadInput = (input) => {
  if (!input) {
    return {};
  }

  if (typeof File !== 'undefined' && input instanceof File) {
    return { file: input };
  }

  if (typeof input === 'object') {
    return input;
  }

  return {};
};

const normalizeAnalysisId = (payload) =>
  payload?.analysis_id || payload?.analysisId || payload?.data?.analysis_id || payload?.data?.analysisId || null;

const normalizeAnalysisPayload = (payload) => {
  if (!payload) {
    return null;
  }

  const response = payload?.data || payload;
  const result = response?.result || response?.data?.result || response?.analysisResult || response;

  if (!result) {
    return null;
  }

  return {
    analysis_id: normalizeAnalysisId(response),
    status: String(response?.status || response?.state || 'COMPLETED').toUpperCase(),
    message: response?.message || '',
    result,
  };
};

const buildFormData = ({ file }) => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

const buildUploadParams = ({ tenantId, sprintId, releaseId, projectId, boardId, sessionId }) => {
  const params = {};

  if (sessionId) {
    params.session_id = sessionId;
  }

  if (tenantId) {
    params.company_id = tenantId;
  }

  if (projectId) {
    params.project_id = projectId;
  }

  if (boardId) {
    params.board_id = boardId;
  }

  if (sprintId) {
    params.sprint_id = sprintId;
  }

  if (releaseId) {
    params.release_id = releaseId;
  }

  return params;
};

export const submitScreenshotForAnalysis = createAsyncThunk(
  'aiUpload/submitScreenshotForAnalysis',
  async (input, { dispatch, rejectWithValue }) => {
      const { file, sprintId, tenantId, projectId, releaseId, boardId } = normalizeUploadInput(input);

    if (!file) {
      return rejectWithValue('Please choose a screenshot to upload.');
    }

    const typeCheck = validateFileType(file);
    if (!typeCheck.valid) {
      return rejectWithValue(typeCheck.message);
    }

    const sizeCheck = validateFileSize(file);
    if (!sizeCheck.valid) {
      return rejectWithValue(sizeCheck.message);
    }

    try {
      dispatch(setUploadStatus('uploading'));
      dispatch(setUploadProgress(0));

      const resolvedTenantId =
        tenantId || sessionStorage.getItem('tenantId') || sessionStorage.getItem('companyId') || undefined;
      const resolvedSprintId = sprintId || sessionStorage.getItem('sprintId') || undefined;
      const resolvedProjectId = projectId || sessionStorage.getItem('projectId') || undefined;
      const resolvedReleaseId = releaseId || sessionStorage.getItem('releaseId') || undefined;
      const resolvedBoardId = boardId || sessionStorage.getItem('boardId') || undefined;
      const resolvedSessionId = getDashboardSessionId();
      const formData = buildFormData({
        file,
      });
      const params = buildUploadParams({
        tenantId: resolvedTenantId,
        sprintId: resolvedSprintId,
        releaseId: resolvedReleaseId,
        projectId: resolvedProjectId,
        boardId: resolvedBoardId,
        sessionId: resolvedSessionId,
      });

      const response = await uploadScreenshot(formData, {
        params,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent?.total) {
            return;
          }

          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(setUploadProgress(progress));
        },
      });

      return {
        analysisId: normalizeAnalysisId(response) || resolvedSessionId,
        uploadId: normalizeAnalysisId(response) || resolvedSessionId,
        status: 'completed',
        message: response?.message || '',
        result: response?.result || response?.upload || response || null,
      };
    } catch (error) {
      return rejectWithValue(formatUploadError(error));
    }
  },
);

export const fetchAnalysisStatus = createAsyncThunk(
  'aiUpload/fetchAnalysisStatus',
  async (input, { rejectWithValue }) => {
    const analysisId =
      typeof input === 'string' ? input : input?.analysisId || input?.analysis_id || input?.id || null;

    if (!analysisId) {
      return rejectWithValue('Missing analysis id.');
    }

    try {
      const response = await getAnalysisStatus(analysisId);
      const normalized = normalizeAnalysisPayload(response);

      if (!normalized) {
        return {
          analysisId,
          status: 'PROCESSING',
          message: 'Analysis in progress',
          result: null,
        };
      }

      return normalized;
    } catch (error) {
      return rejectWithValue(formatUploadError(error));
    }
  },
);

const syncAnalysisState = (state, payload, status) => {
  const normalizedStatus = String(status || payload?.status || 'idle').toLowerCase();
  state.analysisId = payload?.analysisId || payload?.analysis_id || state.analysisId || null;
  state.uploadId = state.analysisId;
  state.status = normalizedStatus;
  state.uploadStatus = normalizedStatus;
  state.summary = payload?.result || payload?.summary || null;
  state.analysisResult = state.summary;
  state.error = null;
  state.statusLoading = false;
  state.polling = normalizedStatus === 'processing';
  state.uploadProgress = normalizedStatus === 'completed' ? 100 : state.uploadProgress;
};

const aiUploadSlice = createSlice({
  name: 'aiUpload',
  initialState,
  reducers: {
    setUploadedFile: (state, action) => {
      const file = action.payload || null;
      state.file = file;
      state.uploadedFile = file;
      state.status = 'idle';
      state.uploadStatus = 'idle';
      state.uploadProgress = 0;
      state.analysisId = null;
      state.uploadId = null;
      state.summary = null;
      state.analysisResult = null;
      state.error = null;
      state.polling = false;
      state.statusLoading = false;
    },
    setUploadStatus: (state, action) => {
      const nextStatus = action.payload || 'idle';
      state.status = nextStatus;
      state.uploadStatus = nextStatus;
      state.polling = nextStatus === 'processing';
    },
    setUploadProgress: (state, action) => {
      const nextProgress = Number(action.payload || 0);
      state.uploadProgress = Number.isNaN(nextProgress) ? 0 : Math.min(Math.max(nextProgress, 0), 100);
    },
    setUploadId: (state, action) => {
      const analysisId = action.payload || null;
      state.analysisId = analysisId;
      state.uploadId = analysisId;
    },
    setAnalysisResult: (state, action) => {
      state.summary = action.payload || null;
      state.analysisResult = action.payload || null;
      if (action.payload) {
        state.status = 'completed';
        state.uploadStatus = 'completed';
        state.polling = false;
        state.error = null;
        state.uploadProgress = 100;
      }
    },
    setUploadError: (state, action) => {
      state.error = action.payload || null;
      state.status = action.payload ? 'failed' : state.status;
      state.uploadStatus = action.payload ? 'failed' : state.uploadStatus;
      state.polling = false;
      state.statusLoading = false;
    },
    setPolling: (state, action) => {
      state.polling = Boolean(action.payload);
    },
    setStatusLoading: (state, action) => {
      state.statusLoading = Boolean(action.payload);
    },
    resetUploadState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitScreenshotForAnalysis.pending, (state) => {
        state.error = null;
        state.summary = null;
        state.analysisResult = null;
        state.analysisId = null;
        state.uploadId = null;
        state.polling = false;
        state.statusLoading = false;
        state.status = 'uploading';
        state.uploadStatus = 'uploading';
      })
      .addCase(submitScreenshotForAnalysis.fulfilled, (state, action) => {
        syncAnalysisState(state, action.payload, action.payload?.status || 'processing');
      })
      .addCase(submitScreenshotForAnalysis.rejected, (state, action) => {
        if (action.meta?.aborted) {
          return;
        }

        state.status = 'failed';
        state.uploadStatus = 'failed';
        state.error = action.payload || action.error?.message || 'Unable to upload the screenshot right now.';
        state.polling = false;
        state.statusLoading = false;
      })
      .addCase(fetchAnalysisStatus.pending, (state) => {
        state.statusLoading = true;
      })
      .addCase(fetchAnalysisStatus.fulfilled, (state, action) => {
        const payloadStatus = String(action.payload?.status || '').toLowerCase();

        if (payloadStatus === 'completed') {
          syncAnalysisState(state, action.payload, 'completed');
          return;
        }

        if (payloadStatus === 'failed') {
          state.analysisId = action.payload?.analysisId || action.payload?.analysis_id || state.analysisId || null;
          state.uploadId = state.analysisId;
          state.status = 'failed';
          state.uploadStatus = 'failed';
          state.summary = null;
          state.analysisResult = null;
          state.error = action.payload?.message || action.payload?.error || 'Analysis failed.';
          state.polling = false;
          state.statusLoading = false;
          return;
        }

        state.analysisId = action.payload?.analysisId || action.payload?.analysis_id || state.analysisId || null;
        state.uploadId = state.analysisId;
        state.status = 'processing';
        state.uploadStatus = 'processing';
        state.polling = true;
        state.statusLoading = false;
      })
      .addCase(fetchAnalysisStatus.rejected, (state, action) => {
        if (action.meta?.aborted) {
          state.statusLoading = false;
          state.polling = false;
          return;
        }

        state.statusLoading = false;
        state.polling = false;
        state.status = 'failed';
        state.uploadStatus = 'failed';
        state.error = action.payload || action.error?.message || 'Unable to load analysis status right now.';
      });
  },
});

export const {
  setUploadedFile,
  setUploadStatus,
  setUploadProgress,
  setUploadId,
  setAnalysisResult,
  setUploadError,
  setPolling,
  setStatusLoading,
  resetUploadState,
} = aiUploadSlice.actions;

export default aiUploadSlice.reducer;
